'use client';

import React, { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import linkIcon from '@/assets/icons/company-news-link-icon.svg';
import notFoundIcon from '@/assets/icons/table-dead-state-icon.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import { useWindowDimensions } from '@/helpers/hooks';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { useGetCompanyNewsQuery } from '@/store/api/dashboardStrapi';

import TabHeader from '../DasboardSidebar/components/TabHeader';
import DeadState from '../DeadState';
import ReportsLoader from '../Reports/CustomerDetailModal/ReportsLoader';

const PRODUCT_NOTIFICATIONS = 'product_notifications';
const CAMPAIGNS_INCENTIVES = 'campaigns_incentives';
const GET_PLUGGED_IN = 'get_plugged_in';

const CompanyNews = () => {
  const { webalias } = useWebAlias();
  const [activeTab, setActiveTab] = useState(CAMPAIGNS_INCENTIVES);
  const { customer } = useAuthContext();
  const { data: companyNews, isLoading } = useGetCompanyNewsQuery();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isMedium = width < 1280 && width >= 768;

  const tabs = [
    { name: 'PRODUCT NOTIFICATIONS', key: PRODUCT_NOTIFICATIONS },
    { name: 'CAMPAIGNS & INCENTIVES', key: CAMPAIGNS_INCENTIVES },
    { name: 'GET PLUGGED-IN!', key: GET_PLUGGED_IN },
  ];

  const getSectionData = () => {
    if (!companyNews) return [];
    switch (activeTab) {
      case PRODUCT_NOTIFICATIONS:
        return companyNews?.data?.productNotifications?.sections || [];
      case CAMPAIGNS_INCENTIVES:
        return companyNews?.data?.campaignAndIncentives?.sections || [];
      case GET_PLUGGED_IN:
        return companyNews?.data?.getPluggedIn?.sections || [];
      default:
        return [];
    }
  };

  const greenBoxText = (() => {
    if (!companyNews) return '';
    const section =
      activeTab === PRODUCT_NOTIFICATIONS
        ? companyNews.data.productNotifications
        : activeTab === CAMPAIGNS_INCENTIVES
        ? companyNews.data.campaignAndIncentives
        : companyNews.data.getPluggedIn;
    return section?.greenBox?.visible ? section?.greenBox?.text : '';
  })();

  const sectionData = getSectionData();

  return (
    <div className="v2-shadow-md v2-p-[24px] md:v2-p-[24px] sm:v2-p-4 xs:v2-p-4 v2-bg-white v2-rounded-xs">
      <div className="v2-pb-5 v2-border-b v2-border-[#E5E5E5] v2-mb-5 v2-flex v2-items-center v2-justify-center">
        <h2 className="v2-text-[#333333] v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl">{companyNews?.title}</h2>
      </div>

      {isLoading ? (
        <div className="v2-flex v2-justify-center v2-items-center md:v2-h-[373px] v2-h-[373px] v2-p-8">
          <ReportsLoader />
        </div>
      ) : (
        <>
          <TabHeader list={tabs} activeTab={activeTab} setActiveTab={setActiveTab} stretchTabs={isMedium} />

          <div
            className={cn('v2-flex v2-flex-col v2-gap-5 v2-mt-5 v2-h-[365px] v2-overflow-auto', {
              'v2-overflow-hidden': sectionData.length === 0,
            })}
          >
            {sectionData.length === 0 ? (
              <div className="v2-flex v2-items-center v2-align-middle v2-justify-center v2-h-[365px]">
                <DeadState contentWrapperClassName="!v2-pt-0" icon={notFoundIcon} title="No Data Found" />
              </div>
            ) : (
              sectionData.map((item, index) => {
                let parsedDescription = '';
                try {
                  parsedDescription = JSON.parse(item.description)?.value || '';
                } catch {
                  parsedDescription = '';
                }

                const itemLink = item.linkType === 'link' ? item.link : item.pdfUrl;

                const resolvedLink = customer?.webAlias
                  ? itemLink?.replace('{0}', customer?.webAlias).replace('%7B0%7D', customer?.webAlias)
                  : '';

                return (
                  <div
                    key={index}
                    className="v2-flex v2-justify-between v2-gap-3 v2-items-center v2-border-b v2-border-[#E5E5E5] v2-pb-5"
                  >
                    <div className="v2-max-w-[246px] md:v2-max-w-[350px]">
                      <h4 className="v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-sm v2-text-[#333333]">
                        {item.title}
                      </h4>
                      <div
                        className="v2-font-sweet-sans-pro v2-text-sm v2-text-[#101828] [&_strong]:v2-font-sweet-sans-pro-semi-bold [&_strong]:v2-font-semibold"
                        dangerouslySetInnerHTML={{ __html: parsedDescription }}
                      />
                    </div>

                    {((item.linkType === 'link' && item.link) || (item.linkType === 'pdf' && item.pdfUrl)) && (
                      <Link
                        href={resolvedLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="v2-flex v2-gap-1.5 v2-font-sweet-sans-pro-medium v2-font-medium v2-text-sm !v2-text-[#123E2E]"
                      >
                        Link <Image src={linkIcon} width={20} height={20} alt="" />
                      </Link>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {greenBoxText && (
            <div className="v2-mt-5 v2-bg-[#F5F7F2] v2-p-3 v2-rounded-xs">
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-[#154734] v2-text-base v2-text-center v2-max-w-none v2-mx-auto md:v2-max-w-[360px]">
                {greenBoxText}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompanyNews;
